import type { Artifact } from '@/api/schemas';
import { formatDate } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { CopyButton } from '@/components/CopyButton';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { artifactFacts, hasSigning, isAndroidBinary, kindIcon, metadataRows } from './artifactModel';
import { useArtifactDownload } from './useArtifactDownload';

interface ArtifactsProps {
  artifacts: Artifact[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  pending: boolean;
}

/**
 * The build's artifacts (`GET /builds/{id}/artifacts`): file, kind, size, sha256, signing certificate and the metadata
 * read from the binary, with a Download per artifact still stored (`GET /artifacts/{id}/download?redirect=false`).
 */
export function BuildArtifacts({ artifacts, isLoading, error, onRetry, pending }: ArtifactsProps) {
  const { busy, download } = useArtifactDownload();

  if (isLoading && !artifacts) return <ArtifactsSkeleton />;
  if (error && !artifacts) return <ErrorState compact error={error} onRetry={onRetry} />;
  if (!artifacts || artifacts.length === 0) return <ArtifactsEmpty pending={pending} />;
  return (
    <ul className="flex flex-col gap-2 p-4 sm:p-5" data-testid="build-artifacts">
      {artifacts.map((a, i) => (
        <ArtifactRow key={a.id} artifact={a} index={i} busy={busy.has(a.id)} onDownload={() => void download(a)} />
      ))}
    </ul>
  );
}

function ArtifactsEmpty({ pending }: { pending: boolean }) {
  return (
    <EmptyState
      compact
      icon="folder_zip"
      title={pending ? 'The app file arrives when the build finishes' : 'No app file'}
      description={pending ? 'Hangar keeps a copy of the app here, with its checksum, its signing certificate and its version details.' : 'The build ended before it produced an app file.'}
    />
  );
}

function ArtifactRow({ artifact: a, index, busy, onDownload }: { artifact: Artifact; index: number; busy: boolean; onDownload: () => void }) {
  return (
    <li data-testid={`build-artifact-${a.kind}`} data-artifact-id={a.id} style={stagger(index)} className="enter flex flex-col gap-2 rounded-lg border bg-secondary/40 p-3">
      {/* On a phone the Download button wraps to its own full-width row, so the file name keeps the whole line. */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-card text-muted-foreground">
          <Icon name={kindIcon(a.kind)} size={20} className={isAndroidBinary(a.kind) ? 'text-android' : undefined} />
        </span>
        <div className="min-w-0 flex-1 basis-56">
          <ArtifactTitle a={a} />
          <ArtifactFacts a={a} />
        </div>
        {!a.deletedAt ? <DownloadButton a={a} busy={busy} onDownload={onDownload} /> : null}
      </div>
      {a.sha256 ? <Sha256Line sha256={a.sha256} /> : null}
      {hasSigning(a) ? <SigningLine signing={a.signing} /> : null}
      <MetadataList a={a} />
    </li>
  );
}

function ArtifactTitle({ a }: { a: Artifact }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="min-w-0 truncate font-mono text-[13px] font-medium text-foreground" title={a.filename}>
        {a.filename}
      </span>
      <Tag className="uppercase">{a.kind}</Tag>
      {a.pinned ? <Tag title="Never expires">pinned</Tag> : null}
      {a.legalHold ? <Tag className="text-awaiting">legal hold</Tag> : null}
      {a.deletedAt ? <Tag className="text-failed">deleted by retention</Tag> : null}
    </div>
  );
}

function ArtifactFacts({ a }: { a: Artifact }) {
  const facts = artifactFacts(a);
  return (
    <div className="tabular flex flex-wrap gap-x-1.5 font-mono text-xs text-subtle">
      {facts.map((fact, i) => (
        <span key={fact} className="whitespace-nowrap">
          {fact}
          {i < facts.length - 1 ? ' ·' : ''}
        </span>
      ))}
    </div>
  );
}

function DownloadButton({ a, busy, onDownload }: { a: Artifact; busy: boolean; onDownload: () => void }) {
  return (
    <Button variant="outline" size="sm" className="max-sm:w-full" data-testid={`build-artifact-download-${a.kind}`} aria-label={`Download ${a.filename}`} loading={busy} onClick={onDownload}>
      {busy ? null : <Icon name="download" size={16} />}
      Download
    </Button>
  );
}

function Sha256Line({ sha256 }: { sha256: string }) {
  return (
    <div className="flex items-center gap-2 font-mono text-[11px] text-subtle">
      <span className="shrink-0">sha256</span>
      <span className="min-w-0 truncate text-muted-foreground" title={sha256} data-testid="build-artifact-sha256">
        {sha256}
      </span>
      <CopyButton text={sha256} label="Copy sha256" size="icon-sm" className="size-7" />
    </div>
  );
}

function SigningLine({ signing }: { signing: Artifact['signing'] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[11px] text-subtle" data-testid="build-artifact-signing">
      <Icon name="shield" size={14} className="shrink-0" />
      {signing?.subject ? <span className="min-w-0 break-words text-muted-foreground">{signing.subject}</span> : null}
      {/* a 59-character fingerprint has no break opportunity: let it break anywhere rather than overflow the card */}
      {signing?.sha1 ? <span className="min-w-0 break-all">SHA-1 {signing.sha1}</span> : null}
      {signing?.validUntil ? <span className="shrink-0">valid until {formatDate(signing.validUntil)}</span> : null}
    </div>
  );
}

function MetadataList({ a }: { a: Artifact }) {
  const meta = metadataRows(a);
  if (meta.length === 0) return null;
  return (
    <dl className="flex flex-wrap gap-x-4 gap-y-0.5 font-mono text-[11px] text-subtle" data-testid="build-artifact-metadata">
      {meta.map(([k, v]) => (
        <div key={k} className="flex min-w-0 max-w-full gap-1">
          <dt className="shrink-0">{k}:</dt>
          <dd className="min-w-0 break-all text-muted-foreground">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The geometry of two artifact rows: icon, name and facts, the Download button, the sha256 line. */
function ArtifactsSkeleton() {
  return (
    <div className="flex flex-col gap-2 p-4 sm:p-5" aria-busy="true" data-testid="build-artifacts-loading">
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-2 rounded-lg border p-3">
          <div className="flex items-center gap-3">
            <Skeleton className="size-9 rounded-lg" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-4 w-3/5" />
              <Skeleton className="h-3 w-2/5" />
            </div>
            <Skeleton className="h-8 w-24" />
          </div>
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}
